import dayjs from 'dayjs';
import { VisitorPass } from '../models/IVisitorPass';
import { AppConfig } from './constants';
import { PassPolicy } from '../models/IPassPolicy';

export const dateFormat = (date: Date) => {
  return dayjs(date).format('MM/DD/YY');
};
export const dtFormat = (date: Date, format: string) => {
  return dayjs(date).format(format);
};
export const getMinDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return dayjs(date).format('YYYY-MM-DD');
};
const getLocalDateTimeFromGMT = (date: Date) => {
  let dCopy = new Date(date);

  const offsetMinutes = dCopy.getTimezoneOffset(),
    offsetMilliseconds = offsetMinutes * 60 * 1000;

  if (!date) {
    dCopy = date = new Date();
  }

  return new Date(dCopy.getTime() + offsetMilliseconds);
};

export const formatDate = (pass: VisitorPass, country: string): string => {
  if (!pass.shoppingDateStart || !pass.shoppingDateEnd) {
    return '---';
  }
  const format = getUserShortDate(country);
  const startDate = dtFormat(pass.shoppingDateStart, format);
  const endDate = dtFormat(pass.shoppingDateEnd, format);
  const formattedDate =
    pass.passStatusId === 1 || pass.passStatusId === 0 || pass.passStatusId === null
      ? startDate === endDate
        ? startDate
        : `${startDate} - ${endDate}`
      : dtFormat(getLocalDateTimeFromGMT(pass.statusTimestamp), format);
  return pass.slotDescription ? formattedDate + ' (' + pass.slotDescription + ')' : formattedDate;
};

export const formatDates = (
  shoppingDateStart: Date,
  shoppingDateEnd: Date,
  statusId: any,
  statusTimestamp: Date,
  slotDescription: any,
): string => {
  if (!shoppingDateStart || !shoppingDateEnd) {
    return '---';
  }
  const startDate = dateFormat(shoppingDateStart);
  const endDate = dateFormat(shoppingDateEnd);
  const formattedDate =
    !statusId || statusId === 1 || statusId === 0 || statusId === null
      ? startDate === endDate
        ? startDate
        : `${startDate} - ${endDate}`
      : dateFormat(getLocalDateTimeFromGMT(statusTimestamp));
  return slotDescription ? formattedDate + ' (' + slotDescription + ')' : formattedDate;
};

export const getCurrencySymbol = (countryCode: number): string => {
  switch (countryCode) {
    case AppConfig.policyLocationIds.japan:
      return '円';
    case AppConfig.policyLocationIds.mexico:
      return 'MXN';
    default:
      return '$';
  }
};
export const isStartDateValid = (startDate: any) => {
  if (startDate == null || startDate === undefined || startDate == '') {
    return false;
  }

  let result = false;
  const dateOne = new Date(startDate);
  const dateTwo = new Date();
  dateTwo.setDate(dateTwo.getDate() - 1);
  dateTwo.setHours(0, 0, 0, 0);
  if (dateOne.getFullYear() > 2010) {
    result = dateOne >= dateTwo;
  }
  return result;
};
export const getSetDates = (
  shoppingDateStart: string,
  shoppingDateEnd: string,
  passpolicy: PassPolicy,
  shoppingDaysOverride: number | null,
  country: string,
) => {
  let eDate = dayjs(shoppingDateEnd).toDate();
  let shoppingDays = passpolicy.shoppingDays;
  const dayInMilliseconds = 86400000;
  const endDate = new Date(passpolicy.shoppingEndDate);
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date(passpolicy.shoppingStartDate);
  startDate.setHours(0, 0, 0, 0);
  const dateIn = dayjs(shoppingDateStart).toDate();
  const endDate_: any | null = null;
  const dateObj = {
    dateAlertText: '',
    endDate: endDate_,
    selectDateRange: 0,
    shoppingDays: shoppingDays,
    isValid: false,
  };
  if (shoppingDaysOverride) {
    eDate = new Date(shoppingDateStart ?? new Date());
    eDate.setTime(eDate.getTime() + shoppingDaysOverride * dayInMilliseconds);
    dateObj.shoppingDays = shoppingDaysOverride;
    shoppingDays = shoppingDaysOverride;
  }
  if (passpolicy.passTypeId == AppConfig.visitTypes.vip) {
    dateObj.endDate = new Date(eDate);
    dateObj.isValid = true;
    return dateObj;
  }
  if (!isStartDateValid(dateIn)) {
    dateObj.endDate = '';
    return dateObj;
  }

  if (dateIn > endDate) {
    dateObj.endDate = '';
    dateObj.dateAlertText = 'PASS_DATE_ALERT_START_DATE_AFTER';
    return dateObj;
  }
  if (dateIn < startDate) {
    dateObj.endDate = '';
    dateObj.dateAlertText = 'PASS_DATE_ALERT_START_DATE_BEFORE';
    return dateObj;
  }

  let numDays = (endDate.getTime() - dateIn.getTime()) / dayInMilliseconds;

  if (passpolicy.overrideShoppingEnd) {
    numDays = shoppingDays;
  }

  if (numDays < shoppingDays - 1) {
    dateIn.setDate(dateIn.getDate() + numDays);
    numDays = Math.round(numDays);
    dateObj.selectDateRange = numDays + 1;
    if (numDays > 1) {
      dateObj.dateAlertText = 'PASS_MODAL_DATE_RANGE_ALTERNATE_PLURAL';
    } else {
      dateObj.dateAlertText = 'PASS_MODAL_DATE_RANGE_ALTERNATE_SINGLE';
    }
    dateObj.shoppingDays = numDays;
  } else {
    dateIn.setDate(dateIn.getDate() + shoppingDays - 1);
    dateObj.selectDateRange = shoppingDays;
    if (shoppingDays > 1) {
      dateObj.dateAlertText = 'PASS_MODAL_DATE_RANGE_MULTIPLE';
    } else {
      dateObj.dateAlertText = 'PASS_MODAL_DATE_RANGE_SINGLE';
    }
    dateObj.shoppingDays = shoppingDays;
  }

  if (dateIn instanceof Date && !isNaN(dateIn.valueOf())) {
    dateObj.endDate = dayjs(dateIn).format('YYYY-MM-DD');
    dateObj.isValid = true;
    return dateObj;
  }
  return dateObj;
};

export const setShortDateByCountry = (country: string) => {
  let formatStr = 'MM/DD/YY';
  if (country) {
    formatStr = getUserShortDate(country);
  }
  return formatStr;
};

export const getUserShortDate = (country: string) => {
  let format;
  switch (country.toUpperCase()) {
    // yyyy/m/d
    case 'CHINA':
    case 'CN': // China
    case 'CHN': // China
    case 'HONG KONGong S.A.R.':
    case 'HONG KONG SAR':
    case 'JAPAN':
    case 'JP': // Japan
    case 'MACAU SAR':
    case 'TAIWAN':
      format = 'YYYY/MM/DD';
      break;

    // yyyy.mm.dd
    case 'KR': // Korea
    case 'KOREA':
    case 'KOREA, REPUBLIC OF':
    case 'HUNGARY':
    case 'HU': // Hungary
      format = 'YYYY.MM.DD';
      break;

    // dd.mm.yyyy
    case 'AUSTRIA':
    case 'CZECH REPUBLIC':
    case 'CROATIA':
    case 'DENMARK':
    case 'GERMANY':
    case 'DE': // Germany
    case 'NORWAY':
    case 'NO': // Norway
    case 'POLAND':
    case 'ROMANIA':
    case 'RUSSIA':
    case 'SWITZERLAND':
    case 'TURKEY':
    case 'TR': // Turkey
      format = 'DD.MM.YYYY';
      break;

    // dd-mm-yyyy
    case 'CANNADA':
    case 'CANADA':
    case 'CA':
    case 'SWEDEN':
    case 'SE': // Sweden
      format = 'DD-MM-YYYY';
      break;

    // dd/mm/yyyy
    case 'ARGENTINA':
    case 'ARG': // Argentina
    case 'AUSTRALIA':
    case 'BELGIUM':
    case 'BE': // Belgium.
    case 'BRAZIL':
    case 'BR': // Brazil
    case 'CHILE':
    case 'CL': // Chile
    case 'COSTA RICA':
    case 'FRANCE':
    case 'FR': // France
    case 'GB': // Great Britain
    case 'GREECE':
    case 'GR': // Greece
    case 'INDIA':
    case 'IN': // India
    case 'INDONESIA':
    case 'IRELAND':
    case 'ISRAEL':
    case 'ITALY':
    case 'IT': // Italy
    case 'MALAYSIA':
    case 'MEXICO':
    case 'MX':
    case 'NETHERLANDS':
    case 'NETHERLANDS, THE':
    case 'NL': // Netherlands
    case 'NEW ZEALAND':
    case 'PORTUGAL':
    case 'SINGAPORE':
    case 'SG': // Singapore
    case 'SPAIN':
    case 'ES': // Spain
    case 'SRI LANKA':
    case 'SOUTH AFRICA':
    case 'THAILAND':
    case 'UNITED KINGDOM':
    case 'URUGUAY':
    case 'VIET NAM':
      format = 'DD/MM/YYYY';
      break;

    // mm/dd/yyyy
    case 'PHILIPPINES':
    case 'UNITED STATES':
    case 'UNITEDSTATES':
    case 'US':
    case 'USA':
    default:
      format = 'MM/DD/YYYY';
      break;
  }
  return format;
};
//convert date to UTC date
export const toUTCDate = (date: Date) => {
  return new Date(date.toUTCString());
};
